import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User, Settings, Shield, CreditCard, LogOut,
  ChevronDown, Crown
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { LanguageSelector } from "@/components/LanguageSelector";

export function UserMenu() {
  const { user, profile, isAdmin, signOut } = useAuth();
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const initials = ((profile?.display_name || user?.email || "U")[0]).toUpperCase();

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate("/auth");
  };

  const menuItems = [
    { label: t("user_menu.profile"), icon: User, to: "/profile", desc: t("user_menu.profile_desc") },
    { label: t("user_menu.account_settings"), icon: Settings, to: "/profile?tab=profile", desc: t("user_menu.account_settings_desc") },
    { label: t("user_menu.security"), icon: Shield, to: "/profile?tab=security", desc: t("user_menu.security_desc") },
    { label: t("user_menu.billing"), icon: CreditCard, to: "/pricing", desc: t("user_menu.billing_desc") },
  ];

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-full border border-border/50 hover:border-primary/40 bg-muted/20 hover:bg-muted/30 transition-all group"
        aria-label="User menu"
        aria-expanded={open}
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs font-bold text-primary-foreground shadow-neon">
          {initials}
        </div>
        <span className="hidden sm:block text-xs font-medium text-foreground max-w-[100px] truncate">
          {profile?.display_name || user.email?.split("@")[0]}
        </span>
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-border/50 bg-popover/95 backdrop-blur-xl shadow-cyber z-50 overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="p-4 border-b border-border/30 bg-muted/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-sm font-bold text-primary-foreground shadow-neon">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{profile?.display_name || "User"}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-1.5">
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-muted/30 transition-colors group"
              >
                <div className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <item.icon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div>
                  <div className="text-xs font-medium text-foreground">{item.label}</div>
                  <div className="text-xs text-muted-foreground">{item.desc}</div>
                </div>
              </Link>
            ))}

            {/* Language Selector */}
            <div className="mt-0.5">
              <LanguageSelector variant="menu-item" />
            </div>

            {/* Admin Dashboard */}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-destructive/10 transition-colors group mt-0.5 border border-destructive/10"
              >
                <div className="w-7 h-7 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Crown className="w-3.5 h-3.5 text-destructive" />
                </div>
                <div>
                  <div className="text-xs font-medium text-destructive">{t("user_menu.admin_dashboard")}</div>
                  <div className="text-xs text-muted-foreground">{t("user_menu.admin_desc")}</div>
                </div>
              </Link>
            )}
          </div>

          {/* Sign Out */}
          <div className="p-1.5 border-t border-border/30">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-destructive/10 transition-colors group"
            >
              <div className="w-7 h-7 rounded-lg bg-muted/30 flex items-center justify-center group-hover:bg-destructive/10 transition-colors">
                <LogOut className="w-3.5 h-3.5 text-muted-foreground group-hover:text-destructive transition-colors" />
              </div>
              <div className="text-xs font-medium text-muted-foreground group-hover:text-destructive transition-colors">{t("user_menu.sign_out")}</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
